import React, { useMemo, useState } from 'react';
import { Modal, TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../Button';
import classes from './pick-list-modal.module.scss';

export type PickListOption = { id: number; name: string; photoUrl?: string | null };

export type PickListModalProps = {
  title: string;
  options: ReadonlyArray<PickListOption> | null;
  disabled?: boolean;
  icon?: IconProp;
  dataTestId?: string;
  optionColor?: 'default' | 'primary' | 'success';
  variant?: 'list' | 'employee-grid';
  fullScreen?: boolean;
  selectedIds?: ReadonlyArray<number>;
  onConfirm?: () => void;
  onPick: (id: number) => void;
  onClose: () => void;
};

// Summary: Bare list picker — {id,name} options as light rows; shared by drawer + printer pickers.
// One-tap pick (no separate confirm step) — row style matches the select-then-confirm pickers
// (e.g. ReportPrinterPickModal) for visual consistency, but onPick fires immediately on tap.
const initialOf = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

const PickListModalComponent: React.FC<PickListModalProps> = ({
  title, options, disabled = false, icon, dataTestId, optionColor = 'default', variant = 'list', fullScreen = false, selectedIds, onConfirm, onPick, onClose,
}) => {
  const [search, setSearch] = useState('');
  const [failedPhotos, setFailedPhotos] = useState<Set<string>>(() => new Set());
  const filteredOptions = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return (options ?? []).filter((option) => !normalized || option.name.toLowerCase().includes(normalized));
  }, [options, search]);
  const isEmployeeGrid = variant === 'employee-grid';
  const isMultiSelect = isEmployeeGrid && selectedIds !== undefined;
  const isFullscreenList = fullScreen && !isEmployeeGrid;

  return <Modal
    opened={options !== null}
    onClose={() => {
      if (!disabled) {
        onClose();
      }
    }}
    title={<strong className={classes.title}>{title}</strong>}
    centered
    fullScreen={fullScreen}
    radius="md"
    zIndex={1200}
    overlayProps={{ opacity: 0.55, blur: 2 }}
    classNames={{
      header: classes.header,
      title: classes.headerTitle,
      content: fullScreen ? classes.contentFull : undefined,
      body: fullScreen ? classes.bodyFull : undefined,
    }}
    closeOnClickOutside={!disabled}
    closeOnEscape={!disabled}
    closeButtonProps={{ disabled, 'data-testid': dataTestId ? `${dataTestId}-close` : undefined } as Record<string, unknown>}
  >
    <div className={classes.body}>
      {isEmployeeGrid && (
        <TextInput
          data-testid={dataTestId ? `${dataTestId}-search` : undefined}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="חיפוש עובד..."
          onContextMenu={(event) => event.preventDefault()}
          onSelect={(event) => event.preventDefault()}
          styles={{ input: { userSelect: 'none', WebkitTouchCallout: 'none' } }}
          leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
        />
      )}
      {isMultiSelect && <div className={classes.selectedTickets} data-testid={dataTestId ? `${dataTestId}-selected` : undefined}>
        {(options ?? []).filter((option) => selectedIds.includes(option.id)).map((option) => (
          <UnstyledButton key={option.id} className={classes.selectedTicket} disabled={disabled} onClick={() => onPick(option.id)} aria-label={`הסר ${option.name}`}>
            <span>{option.name}</span><FontAwesomeIcon icon={faXmark as IconProp} />
          </UnstyledButton>
        ))}
      </div>}
      <div className={isEmployeeGrid ? `${classes.employeeGrid} ${fullScreen ? classes.employeeGridFull : ''}` : `${classes.optionList} ${isFullscreenList ? classes.optionListFull : ''}`}>
        {filteredOptions.map((option) => (
          <UnstyledButton
            key={option.id}
            component="button"
            type="button"
            className={`${isEmployeeGrid ? classes.employeeTile : classes.optionItem} ${isMultiSelect && selectedIds.includes(option.id) ? classes.employeeSelected : ''} ${isFullscreenList ? classes.optionItemTouch : ''} ${optionColor === 'success' ? classes.optionItemSuccess : ''} ${optionColor === 'primary' ? classes.optionItemPrimary : ''}`}
            role={isMultiSelect ? 'checkbox' : undefined}
            aria-checked={isMultiSelect ? selectedIds.includes(option.id) : undefined}
            aria-label={isMultiSelect ? option.name : undefined}
            data-testid={dataTestId ? `${dataTestId}-option-${option.id}` : undefined}
            onClick={() => onPick(option.id)}
            disabled={disabled}
          >
            {isEmployeeGrid ? <>
              {isMultiSelect && selectedIds.includes(option.id) && <span className={classes.selectionBubbles} aria-hidden="true"><i /><i /><i /><i /><i /></span>}
              <span className={classes.employeeAvatar}>
                {option.photoUrl && !failedPhotos.has(option.photoUrl) ? (
                  <img src={option.photoUrl} alt="" onError={() => setFailedPhotos((previous) => new Set(previous).add(option.photoUrl!))} />
                ) : initialOf(option.name)}
              </span>
              <span className={classes.employeeName}>{option.name}</span>
            </> : (
              <span className={classes.optionContent}>
                {icon && <FontAwesomeIcon icon={icon} />}
                <span>{option.name}</span>
              </span>
            )}
          </UnstyledButton>
        ))}
        {isEmployeeGrid && filteredOptions.length === 0 && <p className={classes.empty}>לא נמצאו עובדים</p>}
      </div>
      {isMultiSelect && <Button variant="confirm" onClick={onConfirm} disabled={disabled || selectedIds.length === 0} fullWidth dataTestId={dataTestId ? `${dataTestId}-continue` : undefined}>המשך</Button>}
    </div>
  </Modal>;
};

export const PickListModal = React.memo(PickListModalComponent);
